import { Product } from '@core/models/product.model';

export type PalletAssignmentMode = 'SHARED' | 'EXCLUSIVE' | 'UNKNOWN';

export interface EpcisProductLinePreview {
    lineKey: string;
    sgtinItemKey?: string;
    ndcHint?: string;
    productName?: string;
    lotNumber?: string;
    expiryDate?: string;
    serialNumbers: string[];
    quantityHint: number;
    ssccHints?: string[];
    palletAssignmentMode?: PalletAssignmentMode;
}

export interface EpcisPackagingHierarchyEntry {
    ssccUrn?: string;
    ssccHint?: string;
    childSgtinSerials?: string[];
    childCount?: number;
    shipped?: boolean;
    palletAssignmentMode?: PalletAssignmentMode;
}

export interface EpcisPreviewSummary {
    multiProduct: boolean;
    productLineCount: number;
    palletCount: number;
    palletAssignmentMode: PalletAssignmentMode;
    productLines: EpcisProductLinePreview[];
    packagingHierarchy: EpcisPackagingHierarchyEntry[];
    purchaseOrderHint?: string;
    invoiceHint?: string;
    despatchAdviceHint?: string;
    shippingSsccHint?: string;
    sgtinItemKey?: string;
}

export interface InboundProductLineStaging {
    stagingRowKey: string;
    epcisProductLineKey: string;
    productId: number | null;
    lotNumber: string;
    expiryDate: string;
    quantity: string;
    actualPrice: string;
    palletSscc: string;
    palletWeightKg: string;
    serialNumbers: string[];
}

export function normalizeNdc(ndc?: string | null): string {
    if (!ndc) return '';
    return ndc.replace(/\D/g, '');
}

export function localIdFromBizTransactionUrn(value?: string | null): string {
    if (!value?.trim()) return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('urn:epcglobal:cbv:bt:')) {
        const lastColon = trimmed.lastIndexOf(':');
        return lastColon >= 0 ? trimmed.slice(lastColon + 1) : trimmed;
    }
    return trimmed;
}

export function parseEpcisPreview(previewJson?: string | null): EpcisPreviewSummary | null {
    if (!previewJson?.trim()) return null;
    try {
        const preview = JSON.parse(previewJson) as Record<string, unknown>;
        const productLines = Array.isArray(preview.productLines)
            ? (preview.productLines as Record<string, unknown>[]).map(parseProductLine)
            : [];
        const packagingHierarchy = Array.isArray(preview.packagingHierarchy)
            ? (preview.packagingHierarchy as Record<string, unknown>[]).map(parseHierarchyEntry)
            : [];
        const productLineCount =
            typeof preview.productLineCount === 'number'
                ? preview.productLineCount
                : productLines.length;
        const multiProduct =
            preview.multiProduct === true || productLineCount > 1;
        return {
            multiProduct,
            productLineCount,
            palletCount:
                typeof preview.palletCount === 'number'
                    ? preview.palletCount
                    : packagingHierarchy.length,
            palletAssignmentMode: (preview.palletAssignmentMode as PalletAssignmentMode) || 'UNKNOWN',
            productLines,
            packagingHierarchy,
            purchaseOrderHint: stringHint(preview.purchaseOrderHint),
            invoiceHint: stringHint(preview.invoiceHint),
            despatchAdviceHint: stringHint(preview.despatchAdviceHint),
            shippingSsccHint: stringHint(preview.shippingSsccHint),
            sgtinItemKey: stringHint(preview.sgtinItemKey),
        };
    } catch {
        return null;
    }
}

function stringHint(value: unknown): string | undefined {
    if (value == null) return undefined;
    const text = String(value).trim();
    return text.length > 0 ? text : undefined;
}

function parseProductLine(raw: Record<string, unknown>): EpcisProductLinePreview {
    const serialNumbers = Array.isArray(raw.serialNumbers)
        ? raw.serialNumbers.map((s) => String(s).trim()).filter((s) => s.length > 0)
        : [];
    const ssccHints = Array.isArray(raw.ssccHints)
        ? raw.ssccHints.map((s) => String(s).trim()).filter((s) => s.length > 0)
        : [];
    return {
        lineKey: String(raw.lineKey ?? ''),
        sgtinItemKey: raw.sgtinItemKey != null ? String(raw.sgtinItemKey) : undefined,
        ndcHint: raw.ndcHint != null ? String(raw.ndcHint) : undefined,
        productName: raw.productName != null ? String(raw.productName) : undefined,
        lotNumber: raw.lotNumber != null ? String(raw.lotNumber) : undefined,
        expiryDate: raw.expiryDate != null ? String(raw.expiryDate) : undefined,
        serialNumbers,
        quantityHint:
            typeof raw.quantityHint === 'number' ? raw.quantityHint : serialNumbers.length,
        ssccHints,
        palletAssignmentMode: (raw.palletAssignmentMode as PalletAssignmentMode) || 'UNKNOWN',
    };
}

function parseHierarchyEntry(raw: Record<string, unknown>): EpcisPackagingHierarchyEntry {
    const childSgtinSerials = Array.isArray(raw.childSgtinSerials)
        ? raw.childSgtinSerials.map((s) => String(s).trim()).filter((s) => s.length > 0)
        : undefined;
    return {
        ssccUrn: raw.ssccUrn != null ? String(raw.ssccUrn) : undefined,
        ssccHint: raw.ssccHint != null ? String(raw.ssccHint) : undefined,
        childSgtinSerials,
        childCount: typeof raw.childCount === 'number' ? raw.childCount : undefined,
        shipped: raw.shipped === true,
        palletAssignmentMode: (raw.palletAssignmentMode as PalletAssignmentMode) || undefined,
    };
}

export function stagingRowKey(epcisProductLineKey: string, palletSscc: string): string {
    const sscc = palletSscc?.trim() ?? '';
    if (!sscc) return epcisProductLineKey;
    return `${epcisProductLineKey}@${sscc}`;
}

export function expandPreviewLinesByPallet(summary: EpcisPreviewSummary): Array<{
    stagingRowKey: string;
    epcisProductLineKey: string;
    lotNumber: string;
    expiryDate: string;
    ndcHint?: string;
    productName?: string;
    palletSscc: string;
    serialNumbers: string[];
}> {
    const rows: Array<{
        stagingRowKey: string;
        epcisProductLineKey: string;
        lotNumber: string;
        expiryDate: string;
        ndcHint?: string;
        productName?: string;
        palletSscc: string;
        serialNumbers: string[];
    }> = [];
    for (const line of summary.productLines) {
        const byPallet = groupSerialsByPallet(line.serialNumbers, summary.packagingHierarchy);
        if (byPallet.size === 0) {
            const sscc = line.ssccHints?.[0] ?? '';
            rows.push({
                stagingRowKey: stagingRowKey(line.lineKey, sscc),
                epcisProductLineKey: line.lineKey,
                lotNumber: line.lotNumber ?? '',
                expiryDate: line.expiryDate ?? '',
                ndcHint: line.ndcHint,
                productName: line.productName,
                palletSscc: sscc,
                serialNumbers: [...line.serialNumbers],
            });
            continue;
        }
        for (const [sscc, serials] of byPallet.entries()) {
            rows.push({
                stagingRowKey: stagingRowKey(line.lineKey, sscc),
                epcisProductLineKey: line.lineKey,
                lotNumber: line.lotNumber ?? '',
                expiryDate: line.expiryDate ?? '',
                ndcHint: line.ndcHint,
                productName: line.productName,
                palletSscc: sscc,
                serialNumbers: serials,
            });
        }
    }
    return rows;
}

function groupSerialsByPallet(
    serials: string[],
    hierarchy: EpcisPackagingHierarchyEntry[]
): Map<string, string[]> {
    if (!hierarchy.length || !serials.length) return new Map();
    const result = new Map<string, string[]>();
    for (const serial of serials) {
        const sscc = findSsccForSerial(serial, hierarchy);
        if (!sscc) continue;
        const list = result.get(sscc) ?? [];
        list.push(serial);
        result.set(sscc, list);
    }
    const grouped = new Set([...result.values()].flat());
    if (grouped.size !== serials.length) return new Map();
    return result;
}

function findSsccForSerial(serial: string, hierarchy: EpcisPackagingHierarchyEntry[]): string | null {
    for (const pallet of hierarchy) {
        if (pallet.childSgtinSerials?.includes(serial) && pallet.ssccHint) {
            return pallet.ssccHint;
        }
    }
    return null;
}

export function toDateInputValue(value?: string | null): string {
    if (!value) return '';
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
    }
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
}

export function buildStagingLinesFromPreview(
    previewJson: string | null | undefined,
    products: Product[] = []
): InboundProductLineStaging[] {
    const summary = parseEpcisPreview(previewJson);
    if (!summary || summary.productLines.length === 0) return [];
    const expanded = expandPreviewLinesByPallet(summary);
    return expanded.map((row) => {
        const linePreview: EpcisProductLinePreview = {
            lineKey: row.epcisProductLineKey,
            ndcHint: row.ndcHint,
            productName: row.productName,
            lotNumber: row.lotNumber,
            expiryDate: row.expiryDate,
            serialNumbers: row.serialNumbers,
            quantityHint: row.serialNumbers.length,
        };
        const matched = findCatalogProduct(products, linePreview);
        return {
            stagingRowKey: row.stagingRowKey,
            epcisProductLineKey: row.epcisProductLineKey,
            productId: matched?.id ?? null,
            lotNumber: row.lotNumber,
            expiryDate: toDateInputValue(row.expiryDate),
            quantity: String(row.serialNumbers.length || 1),
            actualPrice: '',
            palletSscc: row.palletSscc,
            palletWeightKg: '',
            serialNumbers: row.serialNumbers,
        };
    });
}

export function findCatalogProduct(
    products: Product[],
    line: EpcisProductLinePreview
): Product | null {
    if (!line.ndcHint) return null;
    const target = normalizeNdc(line.ndcHint);
    return (
        products.find((p) => normalizeNdc(p.ndcCode) === target) ??
        products.find((p) => normalizeNdc(p.gtin) === target) ??
        null
    );
}

export function resolvePalletSsccForLine(line: EpcisProductLinePreview): string {
    if (line.ssccHints && line.ssccHints.length > 0) {
        return line.ssccHints[0];
    }
    return '';
}

export function productLineLabel(line: EpcisProductLinePreview): string {
    const name = line.productName || line.ndcHint || line.lineKey;
    const ndc = line.ndcHint ? ` (NDC ${line.ndcHint})` : '';
    const lot = line.lotNumber ? `, lot ${line.lotNumber}` : '';
    const qty = ` — ${line.quantityHint} serials`;
    const mode = line.palletAssignmentMode ? ` [${line.palletAssignmentMode}]` : '';
    return `${name}${ndc}${lot}${qty}${mode}`;
}

export function palletModeBannerText(summary: EpcisPreviewSummary): string {
    const { productLineCount, palletCount, palletAssignmentMode } = summary;
    const sharedSscc = summary.packagingHierarchy[0]?.ssccHint;
    if (palletAssignmentMode === 'SHARED') {
        const ssccPart = sharedSscc ? ` (${sharedSscc})` : '';
        return `${productLineCount} products on shared pallet${ssccPart} — all lines will be imported into one batch.`;
    }
    if (palletAssignmentMode === 'EXCLUSIVE' && palletCount > 1) {
        return `${productLineCount} products on ${palletCount} pallets — all lines will be imported into one batch.`;
    }
    if (palletAssignmentMode === 'UNKNOWN') {
        return `${productLineCount} product(s) — confirm pallet SSCC on each row before approve.`;
    }
    return `${productLineCount} product(s) from EPCIS — all lines will be imported into one batch.`;
}

export function serialsForProductLine(
    previewJson: string | null | undefined,
    lineKey: string | null | undefined
): string[] {
    const summary = parseEpcisPreview(previewJson);
    if (!summary || !lineKey) return [];
    const line = summary.productLines.find((l) => l.lineKey === lineKey);
    return line?.serialNumbers ?? [];
}

export function findProductLine(
    previewJson: string | null | undefined,
    lineKey: string | null | undefined
): EpcisProductLinePreview | null {
    const summary = parseEpcisPreview(previewJson);
    if (!summary || !lineKey) return null;
    return summary.productLines.find((l) => l.lineKey === lineKey) ?? null;
}

export function productLinesToJson(lines: InboundProductLineStaging[]): string {
    return JSON.stringify(
        lines.map((line) => ({
            stagingRowKey: line.stagingRowKey,
            epcisProductLineKey: line.epcisProductLineKey,
            productId: line.productId ?? undefined,
            lotNumber: line.lotNumber,
            expiryDate: line.expiryDate || undefined,
            quantity: Number(line.quantity) || line.serialNumbers.filter((s) => s.trim()).length || 1,
            actualPrice: line.actualPrice.trim() !== '' ? Number(line.actualPrice) : undefined,
            palletSscc: line.palletSscc.trim() || undefined,
            palletWeightKg: line.palletWeightKg.trim() !== '' ? Number(line.palletWeightKg) : undefined,
            serializedItems: line.serialNumbers
                .filter((s) => s.trim())
                .map((serialNumber) => ({ serialNumber: serialNumber.trim() })),
        }))
    );
}

function normalizeExpiryForCompare(raw?: string | null): string | null {
    if (!raw?.trim()) return null;
    const trimmed = raw.trim();
    if (/^\d{6}$/.test(trimmed)) {
        return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-01`;
    }
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
        return `${trimmed}-01`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
    }
    return trimmed.toUpperCase();
}

/** Compare operator staging (or scan) lot/expiry against supplier EPCIS ILMD hints. */
export function findIlmdStagingMismatches(
    supplierLot?: string | null,
    supplierExpiry?: string | null,
    stagedLot?: string | null,
    stagedExpiry?: string | null
): string[] {
    const warnings: string[] = [];
    if (!stagedLot?.trim()) {
        return warnings;
    }
    if (supplierLot?.trim() && supplierLot.trim().toLowerCase() !== stagedLot.trim().toLowerCase()) {
        warnings.push(
            `Staged lot ${stagedLot.trim()} does not match supplier ILMD lot ${supplierLot.trim()}`
        );
    }
    const normSupplier = normalizeExpiryForCompare(supplierExpiry);
    const normStaged = normalizeExpiryForCompare(stagedExpiry);
    if (normSupplier && normStaged && normSupplier !== normStaged) {
        warnings.push(
            `Staged expiry ${normStaged} does not match supplier ILMD expiry ${normSupplier}`
        );
    }
    return warnings;
}

export function findIlmdScanMismatches(
    supplierLot?: string | null,
    supplierExpiry?: string | null,
    scannedLot?: string | null,
    scannedExpiry?: string | null
): string[] {
    const warnings: string[] = [];
    if (!scannedLot?.trim()) {
        return warnings;
    }
    if (supplierLot?.trim() && supplierLot.trim().toLowerCase() !== scannedLot.trim().toLowerCase()) {
        warnings.push(
            `Scanned lot ${scannedLot.trim()} does not match supplier ILMD lot ${supplierLot.trim()}`
        );
    }
    const normSupplier = normalizeExpiryForCompare(supplierExpiry);
    const normScanned = normalizeExpiryForCompare(scannedExpiry);
    if (normSupplier && normScanned && normSupplier !== normScanned) {
        warnings.push(
            `Scanned expiry ${normScanned} does not match supplier ILMD expiry ${normSupplier}`
        );
    }
    return warnings;
}

export function productLinesFromJson(json?: string | null): InboundProductLineStaging[] {
    if (!json?.trim()) return [];
    try {
        const parsed = JSON.parse(json) as Record<string, unknown>[];
        if (!Array.isArray(parsed)) return [];
        return parsed.map((row) => ({
            stagingRowKey: String(row.stagingRowKey ?? ''),
            epcisProductLineKey: String(row.epcisProductLineKey ?? ''),
            productId: typeof row.productId === 'number' ? row.productId : null,
            lotNumber: String(row.lotNumber ?? ''),
            expiryDate: toDateInputValue(row.expiryDate as string),
            quantity: row.quantity != null ? String(row.quantity) : '1',
            actualPrice: row.actualPrice != null ? String(row.actualPrice) : '',
            palletSscc: String(row.palletSscc ?? ''),
            palletWeightKg: row.palletWeightKg != null ? String(row.palletWeightKg) : '',
            serialNumbers: Array.isArray(row.serializedItems)
                ? (row.serializedItems as { serialNumber?: string }[])
                      .map((s) => s.serialNumber?.trim() ?? '')
                      .filter(Boolean)
                : [],
        }));
    } catch {
        return [];
    }
}
